import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import Grid from "@mui/material/Grid";
import Item from "../../../shared/item/Item";
import { TextValidator, ValidatorForm } from "react-material-ui-form-validator";
import ValidationRules from "../../../common/ValidationRules";
import {
  Accordion,
  AccordionSummary,
  FormControl,
  Button,
} from "@mui/material";
import { withTranslation } from "react-i18next";
import RectangleEdit from "../../../shared/rectangleEdit/RectangleEdit";
import crStyles from "../../customerHandling/crStyles";
import NamePlateSettingsClient from "../../../clients/NamePlateSettingsClient";
import fordStyles from "./NamePlateSettings-Ford.module.scss";
import lincolnStyles from "./NamePlateSettings-Lincoln.module.scss";
import Clear from "@mui/icons-material/Clear";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { logMsg } from "../../../common/Logger";
import {
  displayError,
  ERROR_PROCESSING,
  is404,
  isProcessingError,
  PAGE_ERROR,
} from "../../../errors/ErrorConstants";
import SortingIcon from "../../../shared/sortingIcon/SortingIcon";
import { SortSubGroupData } from "../../../utils/SortUtil";
import { IS_FORD_BRAND } from "../../../utils/EmpUtil";
import PublishButton from "../PublishButton";
import ConfirmDialog from "../../../shared/confirmDialog/ConfirmDialog";
import { BRAND_NAMES } from "../../../common/Constants";
import * as AnalyticsService from "../../../shared/services/analytics/AnalyticsService";
import EMPSelectField from "../../../shared/components/EMPSelectField/EMPSelectField";
import CapitalizeFirstWord from "../../../utils/CapitalizeFirstWord";
import CapitalizeEachWord from "../../../utils/CapitalizeEachWord";

const nameplate = "NAMEPLATE";
const NamePlateSettings = (props) => {
  const { hideOrShow, t, user } = props;
  const isFordBrand = IS_FORD_BRAND(user.brand);
  const styles = isFordBrand ? fordStyles : lincolnStyles;

  const [namePlateSettings, setNamePlateSettings] = useState([]);
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [hasMarkupChanged, setHasMarkupChanged] = useState(false);
  const [hasLaborRateChanged, setHasLaborRateChanged] = useState(false);
  const [markupEditEnabled, setMarkupEditEnabled] = useState(false);
  const [laborRateEditEnabled, setLaborRateEditEnabled] = useState(false);
  const [enablePublishBtn, setEnablePublishBtn] = useState(false);
  const [overAllMarkup, setOverAllMarkup] = useState("");
  const [overAllLaborRate, setOverAllLaborRate] = useState("");
  const [originalMarkup, setOriginalMarkup] = useState("");
  const [originalLaborRate, setOriginalLaborRate] = useState("");
  const [hasError, setHasError] = useState([]);
  const [sortKey, setSortKey] = useState(null);
  const [sortDirection, setSortDirection] = useState(null);
  const [overridesApplied, setOverridesApplied] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState(false);
  const [modelYearGrouping, setModelYearGrouping] = useState(null);
  const [vehicleLines, setVehicleLines] = useState([]);
  const [modelYears, setModelYears] = useState([]);
  const [model, setModel] = useState(null);
  const [year, setYear] = useState(null);
  const [catalogId, setCatalogId] = useState(null);

  const enableOrDisableButton = (
    nextMarkup = overAllMarkup,
    nextLaborRate = overAllLaborRate,
    nextMarkupEditEnabled = markupEditEnabled,
    nextLaborRateEditEnabled = laborRateEditEnabled
  ) =>
    (nextLaborRateEditEnabled && nextLaborRate !== "") ||
    (nextMarkupEditEnabled && nextMarkup !== "");

  const checkReleasedStatus = (settings) => {
    let released = true;
    settings.forEach((record) => {
      if (!released) {
        return;
      }
      record.namePlateCategorySubGroup.forEach((subGroup) => {
        if (!subGroup.released) {
          released = false;
        }
      });
    });
    return released;
  };

  const setOriginalData = (settings) => {
    const updatedSettings = settings.map((record) => ({
      ...record,
      expand: false,
    }));
    const nextMarkup =
      updatedSettings.length > 0
        ? updatedSettings[0].namePlateCategorySubGroup[0].markup
        : "";
    const nextLaborRate =
      updatedSettings.length > 0
        ? updatedSettings[0].namePlateCategorySubGroup[0].laborRate
        : "";

    setNamePlateSettings(updatedSettings);
    setOriginalMarkup(nextMarkup);
    setOverAllMarkup(nextMarkup);
    setOriginalLaborRate(nextLaborRate);
    setOverAllLaborRate(nextLaborRate);
  };

  const handleApiError = (error) => {
    logMsg("reason", error);
    if (is404(error)) {
      setHasError([PAGE_ERROR]);
    } else if (isProcessingError(error)) {
      setHasError([ERROR_PROCESSING]);
    }
  };

  const getNamePlateSettings = (selectedCatalogId = catalogId) => {
    hideOrShow(true);
    NamePlateSettingsClient.getDealerNamePlateAdjustments(user, selectedCatalogId)
      .then((data) => {
        const released = checkReleasedStatus(data);
        setOriginalData(data);
        setEnablePublishBtn(!released);
      })
      .catch(handleApiError)
      .finally(() => {
        hideOrShow(false);
      });
  };

  const getAllVehicleLines = () => {
    hideOrShow(true);
    NamePlateSettingsClient.getAllVehicleLines(
      user,
      IS_FORD_BRAND(user.brand) ? BRAND_NAMES.FORD : BRAND_NAMES.LINCOLN
    )
      .then((grouping) => {
        const lines = Object.keys(grouping);
        const initialModel = lines[0];
        const initialModelYears = grouping[initialModel];
        const initialYear = initialModelYears[0].year;
        const initialCatalogId = initialModelYears[0].catalogId;

        setModelYearGrouping(grouping);
        setVehicleLines(lines);
        setModel(initialModel);
        setModelYears(initialModelYears);
        setYear(initialYear);
        setCatalogId(initialCatalogId);
        getNamePlateSettings(initialCatalogId);
      })
      .catch(handleApiError)
      .finally(() => {
        hideOrShow(false);
        setIsDataLoaded(true);
      });
  };

  useEffect(() => {
    AnalyticsService.trackPageLoadEvent("fv:emp:accessories pricing:nameplate", user);
    ValidationRules();
    getAllVehicleLines();
  }, []);

  const onModelChange = (event) => {
    if (event) {
      event.preventDefault();
    }

    const selectedModel = event.target.value;
    const selectedModelYears = modelYearGrouping[selectedModel];
    const selectedYear = selectedModelYears[0].year;
    const selectedCatalogId = selectedModelYears[0].catalogId;

    setModel(selectedModel);
    setModelYears(selectedModelYears);
    setYear(selectedYear);
    setCatalogId(selectedCatalogId);
    getNamePlateSettings(selectedCatalogId);
  };

  const onYearChange = (event) => {
    if (event) {
      event.preventDefault();
    }

    const selectedYear = event.target.value;
    const selectedCatalogId = modelYears?.find(
      (vehicle) => String(vehicle.year) === selectedYear
    )?.catalogId;

    setYear(selectedYear);
    setCatalogId(selectedCatalogId);
    getNamePlateSettings(selectedCatalogId);
  };

  const updateMarkupEditEnabled = () => {
    user.showWarning(enablePublishBtn);
    const nextMarkupEditEnabled = !markupEditEnabled;
    const nextHasMarkupChanged = !hasMarkupChanged;
    const nextMarkup = originalMarkup;

    setOverAllMarkup(nextMarkup);
    setMarkupEditEnabled(nextMarkupEditEnabled);
    setHasMarkupChanged(nextHasMarkupChanged);
    setEnablePublishBtn(enableOrDisableButton(nextMarkup, overAllLaborRate, nextMarkupEditEnabled, laborRateEditEnabled));
    setOverridesApplied(false);
  };

  const updateLaborRateEditEnabled = () => {
    user.showWarning(enablePublishBtn);
    const nextLaborRateEditEnabled = !laborRateEditEnabled;
    const nextHasLaborRateChanged = !hasLaborRateChanged;
    const nextLaborRate = originalLaborRate;

    setOverAllLaborRate(nextLaborRate);
    setLaborRateEditEnabled(nextLaborRateEditEnabled);
    setHasLaborRateChanged(nextHasLaborRateChanged);
    setEnablePublishBtn(enableOrDisableButton(overAllMarkup, nextLaborRate, markupEditEnabled, nextLaborRateEditEnabled));
    setOverridesApplied(false);
  };

  const sortColumns = (sortByCol) => {
    let sortDir = "ASC";
    if (sortByCol === sortKey) {
      sortDir = sortDirection === "ASC" ? "DSC" : "ASC";
    }

    SortSubGroupData(
      sortByCol,
      sortDir,
      "alphanumeric",
      namePlateSettings,
      (sortedData) => {
        setSortDirection(sortDir);
        setSortKey(sortByCol);
        setNamePlateSettings(sortedData);
      }
    );
  };

  const updateModel = (event) => {
    user.showWarning(enablePublishBtn);
    let nextMarkup = overAllMarkup;
    let nextLaborRate = overAllLaborRate;
    if (event.target.name === "markup") {
      nextMarkup = event.target.value;
    } else {
      nextLaborRate = event.target.value;
    }

    setOverAllMarkup(nextMarkup);
    setOverAllLaborRate(nextLaborRate);
    setHasMarkupChanged(true);
    setHasLaborRateChanged(true);
    setEnablePublishBtn(enableOrDisableButton(nextMarkup, nextLaborRate));
  };

  const flipIcon = (indexIcon) => {
    const updatedSettings = namePlateSettings.map((record, index) =>
      indexIcon === index ? { ...record, expand: !record.expand } : record
    );
    setNamePlateSettings(updatedSettings);
  };

  const handleConfirm = (e) => {
    if (e) {
      e.preventDefault();
    }
    setConfirmDialog(true);
  };

  const handleClose = () => {
    setConfirmDialog(false);
  };

  const handleSubmit = () => {
    hideOrShow(true);
    const updatedNameSettings = namePlateSettings.map((group) => ({
      groupName: group.groupName,
      namePlateCategorySubGroup: group.namePlateCategorySubGroup.map((subGroup) => ({
        ...subGroup,
        markup: Number.parseFloat(overAllMarkup),
        laborRate: Number.parseFloat(overAllLaborRate),
        laborRateType:
          laborRateEditEnabled && overAllLaborRate ? nameplate : subGroup.laborRateType,
        markupType: markupEditEnabled && overAllMarkup ? nameplate : subGroup.markupType,
      })),
    }));

    NamePlateSettingsClient.saveDealerBrandAdjustments(
      user,
      catalogId,
      updatedNameSettings,
      laborRateEditEnabled && overAllLaborRate !== "",
      markupEditEnabled && overAllMarkup !== ""
    )
      .then(() => {
        setOriginalData(updatedNameSettings);
        setEnablePublishBtn(false);
        setMarkupEditEnabled(false);
        setLaborRateEditEnabled(false);
        setOverridesApplied(true);
        setConfirmDialog(false);
      })
      .catch(handleApiError)
      .finally(() => {
        hideOrShow(false);
        user.showWarning(false);
      });
  };

  const confirmDialogText = useMemo(
    () => ({
      title: t("CategorySettings.dialogConfirmTitle"),
      bodyText: t("CategorySettings.dialogConfirmBody"),
      confirm: isFordBrand
        ? t("CategorySettings.dialogFordPublish")
        : t("CategorySettings.dialogLincolnConfirm"),
      cancel: isFordBrand
        ? t("CategorySettings.dialogFordGoBack")
        : t("CategorySettings.dialogLincolnCancel"),
    }),
    [isFordBrand, t]
  );

  if (displayError(hasError)) {
    throw new Error(hasError[0]);
  }

  return (
    isDataLoaded && (
      <>
        <ValidatorForm onSubmit={(e) => handleConfirm(e)}>
          <div className="pagePadding">
            <div className={styles.title}>{t("NamePlate.subHeader")}</div>
            <div className={styles.dropdownsWrapper}>
              <div className={styles.dropdownDiv}>
                {isFordBrand && <div className={styles.subTitle}>{t("NamePlate.model")}</div>}
                <ModelDropDown
                  {...props}
                  isFordBrand={isFordBrand}
                  onModelChange={onModelChange}
                  model={model}
                  vehicleLines={vehicleLines}
                />
              </div>
              <div className={styles.dropdownDiv}>
                {isFordBrand && <div className={styles.subTitle}>{t("NamePlate.year")}</div>}
                <YearDropDown
                  {...props}
                  isFordBrand={isFordBrand}
                  onYearChange={onYearChange}
                  year={year}
                  modelYears={modelYears}
                />
              </div>
            </div>
          </div>
          <NamePlateRowHeader
            {...props}
            styles={styles}
            updateMarkupEditEnabled={updateMarkupEditEnabled}
            updateLaborRateEditEnabled={updateLaborRateEditEnabled}
            updateModel={updateModel}
            sortColumns={sortColumns}
            sortKey={sortKey}
            sortDirection={sortDirection}
            markupEditEnabled={markupEditEnabled}
            laborRateEditEnabled={laborRateEditEnabled}
            overAllMarkup={overAllMarkup}
            overAllLaborRate={overAllLaborRate}
          />
          {namePlateSettings.map((namePlate, index) => (
            <Accordion
              defaultExpanded={true}
              key={namePlate.groupName}
              onChange={() => flipIcon(index)}
            >
              <AccordionSummary
                style={
                  isFordBrand
                    ? crStyles.accessoriesHeaderBarBg
                    : crStyles.accessoriesHeaderBarBgLincoln
                }
                data-testid={"categoryPanel" + index}
              >
                <div className={styles.expansionBar}>
                  <Grid container>
                    <Grid item xs={10} style={crStyles.gridItem}>
                      {CapitalizeFirstWord(namePlate.groupName)} (
                      {namePlate.namePlateCategorySubGroup.length})
                    </Grid>
                    <Grid item xs={2} style={crStyles.dropdown}>
                      {namePlate.expand ? (
                        <ExpandMoreIcon data-testid={"more" + namePlate.groupName} />
                      ) : (
                        <ExpandLessIcon data-testid={"less" + namePlate.groupName} />
                      )}
                    </Grid>
                  </Grid>
                </div>
                <br />
              </AccordionSummary>
              {namePlate.namePlateCategorySubGroup.length > 0 &&
                namePlate.namePlateCategorySubGroup.map((record) => (
                  <div key={record.subGroupName} className={styles.categoryRow}>
                    <Grid container>
                      <Grid item xs={4} className={styles.brand}>
                        <div className={styles.brandName}>{record.subGroupName}</div>
                      </Grid>
                      <Grid item xs={2}>
                        <div className={styles.brandValue}>
                          <p className={styles.brandText}>
                            {" "}
                            {overAllMarkup === "" || !hasMarkupChanged
                              ? record.markup
                              : overAllMarkup}
                            &#37;
                          </p>
                        </div>
                      </Grid>
                      <Grid item xs={2}>
                        <div className={styles.brandValue}>
                          <p className={styles.brandText}>
                            {CapitalizeFirstWord(record.markupType)}
                          </p>
                        </div>
                      </Grid>
                      <Grid item xs={2}>
                        <div className={styles.brandValue}>
                          <p className={styles.brandText}>
                            {" "}
                            &#36;
                            {overAllLaborRate === "" || !hasLaborRateChanged
                              ? record.laborRate
                              : overAllLaborRate}
                          </p>
                        </div>
                      </Grid>
                      <Grid item xs={2}>
                        <div className={styles.brandValue}>
                          <p className={styles.brandText}>
                            {CapitalizeFirstWord(record.laborRateType)}
                          </p>
                        </div>
                      </Grid>
                    </Grid>
                  </div>
                ))}
            </Accordion>
          ))}
          <PublishButton
            {...props}
            styles={styles}
            overridesApplied={overridesApplied}
            enablePublishBtn={enablePublishBtn && catalogId}
          />
        </ValidatorForm>
        <ConfirmDialog
          open={confirmDialog}
          showWarningIcon={true}
          dialogProps={confirmDialogText}
          onConfirm={handleSubmit}
          onCancel={handleClose}
          boldTitle={true}
          onClose={handleClose}
          disableConfirm={
            !user.permissions.rolePermissions.hasOwnProperty("accessoriesSubmit")
          }
          {...props}
        />
      </>
    )
  );
};

const NamePlateRowHeader = (props) => {
  const { styles } = props;
  const isMarkupReadOnly = !props.markupEditEnabled;
  const isLaborRateReadOnly = !props.laborRateEditEnabled;
  return (
    <>
      <div className={styles.namePlateHeader}>
        <Grid container spacing={0}>
          <Grid item xs={4}>
            <div className={`${styles.alignLeft} ${styles.label}`}>
              <Item value={props.t("NamePlate.category")} />
            </div>
          </Grid>
          <Grid item xs={2}>
            <div className={styles.markupTypeLabel}>
              <Item value={props.t("BrandSettings.markup")} />
              {isMarkupReadOnly ? (
                <RectangleEdit
                  dataTestId="markupEditBtn"
                  onEditClick={props.updateMarkupEditEnabled}
                />
              ) : (
                <Button
                  size="small"
                  name="Cancel"
                  data-testid="cancelMarkupRate"
                  onClick={props.updateMarkupEditEnabled}
                >
                  <Clear />
                </Button>
              )}
            </div>
          </Grid>
          <Grid item xs={2} onClick={() => props.sortColumns("markupType")}>
            <div className={styles.markupTypeLabel}>
              <Item value={props.t("CategorySettings.markUpType")} />
              <SortingIcon
                currentKey="markupType"
                sortKey={props.sortKey}
                sortDirection={props.sortDirection}
              />
            </div>
          </Grid>
          <Grid item xs={2}>
            <div className={styles.markupTypeLabel}>
              <Item value={props.t("BrandSettings.laborRate")} />
              {isLaborRateReadOnly ? (
                <RectangleEdit
                  dataTestId="laborRateEditBtn"
                  onEditClick={props.updateLaborRateEditEnabled}
                />
              ) : (
                <Button
                  size="small"
                  name="Cancel"
                  data-testid="cancelLaborRateBtn"
                  onClick={props.updateLaborRateEditEnabled}
                >
                  <Clear />
                </Button>
              )}
            </div>
          </Grid>
          <Grid item xs={2} onClick={() => props.sortColumns("laborRateType")}>
            <div className={styles.markupTypeLabel}>
              <Item value={props.t("CategorySettings.laborRateType")} />
              <SortingIcon
                currentKey="laborRateType"
                sortKey={props.sortKey}
                sortDirection={props.sortDirection}
              />
            </div>
          </Grid>
        </Grid>
      </div>
      <div className={styles.namePlateTextDiv}>
        <Grid container spacing={0}>
          <Grid item xs={4} />
          <Grid item xs={2}>
            {props.markupEditEnabled && (
              <div className={styles.laborRateRow}>
                <div className={styles.headerPercentage}>&#37;</div>
                <div className={styles.textBox}>
                  <TextValidator
                    value={props.overAllMarkup}
                    data-testid="markup"
                    name="markup"
                    placeholder="--"
                    validators={[
                      "required",
                      String.raw`matchRegexp:^^[+-d\b.]+$`,
                      "isSignedFloatWithTwoDecimalPlaces",
                    ]}
                    errorMessages={[
                      props.t("Error.requiredField"),
                      props.t("Error.invalidInput"),
                      props.t("Error.invalidInput"),
                      props.t("Error.invalidInput"),
                    ]}
                    onChange={(e) => props.updateModel(e)}
                  />
                </div>
              </div>
            )}
          </Grid>
          <Grid item xs={2} />
          <Grid item xs={2}>
            {props.laborRateEditEnabled && (
              <div className={styles.laborRateRow}>
                <div className={styles.headerCurrency}>&#36;</div>
                <div className={styles.textBox}>
                  <TextValidator
                    value={props.overAllLaborRate}
                    data-testid="laborRate"
                    name="laborRate"
                    placeholder="--"
                    validators={[
                      "required",
                      String.raw`matchRegexp:^[0-9\b.]+$`,
                      "isNumber",
                      "isNumericWithTwoDecimalPlaces",
                    ]}
                    errorMessages={[
                      props.t("Error.requiredField"),
                      props.t("Error.invalidInput"),
                      props.t("Error.invalidInput"),
                      props.t("Error.invalidInput"),
                    ]}
                    onChange={(e) => props.updateModel(e)}
                  />
                </div>
              </div>
            )}
          </Grid>
        </Grid>
      </div>
    </>
  );
};

const ModelDropDown = (props) => {
  const menuItems = [];
  if (!props.isFordBrand) {
    menuItems.push({
      text: props.t("NamePlate.model"),
      value: "model",
      disabled: true,
    });
  }
  if (props.vehicleLines) {
    props.vehicleLines.forEach((vehicleLine) => {
      menuItems.push({
        text: CapitalizeEachWord(vehicleLine).replace("Mach-e", "Mach-E"),
        value: vehicleLine,
      });
    });
  }
  return (
    <FormControl id="menu" fullWidth={true}>
      <EMPSelectField
        testId="select"
        value={props.model}
        onChange={(event) => props.onModelChange(event)}
        menuItems={menuItems}
      />
    </FormControl>
  );
};

const YearDropDown = (props) => {
  const menuItems = [];
  if (!props.isFordBrand) {
    menuItems.push({
      text: props.t("NamePlate.year"),
      value: "year",
      id: "year",
      disabled: true,
    });
  }
  if (props.modelYears) {
    props.modelYears.forEach((modelYear) => {
      menuItems.push({
        text: modelYear.year,
        value: modelYear.year,
        id: modelYear.year,
      });
    });
  }
  return (
    <FormControl id="menu">
      <EMPSelectField
        testId="select"
        value={props.year}
        onChange={(event) => props.onYearChange(event)}
        menuItems={menuItems}
      />
    </FormControl>
  );
};

NamePlateSettings.propTypes = {
  hideOrShow: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
  user: PropTypes.shape({
    brand: PropTypes.string,
    showWarning: PropTypes.func.isRequired,
    permissions: PropTypes.shape({
      rolePermissions: PropTypes.object.isRequired,
    }).isRequired,
  }).isRequired,
};

NamePlateRowHeader.propTypes = {
  t: PropTypes.func.isRequired,
  styles: PropTypes.object.isRequired,
  updateMarkupEditEnabled: PropTypes.func.isRequired,
  updateLaborRateEditEnabled: PropTypes.func.isRequired,
  updateModel: PropTypes.func.isRequired,
  sortColumns: PropTypes.func.isRequired,
  sortKey: PropTypes.string,
  sortDirection: PropTypes.string,
  markupEditEnabled: PropTypes.bool.isRequired,
  laborRateEditEnabled: PropTypes.bool.isRequired,
  overAllMarkup: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  overAllLaborRate: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

ModelDropDown.propTypes = {
  t: PropTypes.func.isRequired,
  isFordBrand: PropTypes.bool.isRequired,
  onModelChange: PropTypes.func.isRequired,
  model: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  vehicleLines: PropTypes.arrayOf(PropTypes.string),
};

YearDropDown.propTypes = {
  t: PropTypes.func.isRequired,
  isFordBrand: PropTypes.bool.isRequired,
  onYearChange: PropTypes.func.isRequired,
  year: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  modelYears: PropTypes.arrayOf(
    PropTypes.shape({
      year: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      catalogId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
        .isRequired,
    })
  ),
};

export default withTranslation("emp")(NamePlateSettings);
